import { useSyncExternalStore } from 'react';
import type { ReactPortal } from 'react';
import { createPortal } from 'react-dom';

import { whiteAlpha } from '@/config/appColors';
import { PrimaryIconButton } from '@/presentation/widgets/PrimaryIconButton';
import { CustomSnackbar } from './CustomSnackbar';
import { SnackbarManager } from './snackbarManager';
import styles from './SnackbarHost.module.css';

/**
 * Слой тостов: то, что в Dart делал `ToastificationWrapper` над `MaterialApp`.
 *
 * Хост монтируется один раз рядом с приложением и рисуется порталом в
 * `document.body`, поэтому поднять тост может кто угодно — контроллер,
 * репозиторий, обработчик ошибок, — как и Dart-синглтон.
 */

/** ToastCloseButton: PrimaryIconButton(iconSize: 15, size: 59). */
const CLOSE_ICON_SIZE = 15;
const CLOSE_BUTTON_SIZE = 59;

/** Colors.white.withValues(alpha: 0.4) у overlayColor кнопки закрытия. */
const CLOSE_OVERLAY_COLOR = whiteAlpha(0.4);

export function SnackbarHost(): ReactPortal | null {
  const manager = SnackbarManager.instance;
  const items = useSyncExternalStore(
    manager.subscribe,
    manager.getSnapshot,
    manager.getSnapshot,
  );

  if (items.length === 0) return null;

  return createPortal(
    <div className={styles.host}>
      {items.map((item) => (
        <div
          key={item.id}
          className={item.leaving ? `${styles.item} ${styles.leaving}` : styles.item}
        >
          <div className={styles.content}>
            <CustomSnackbar
              type={item.type}
              title={item.title}
              description={item.description}
              code={item.code}
              onRetry={item.onRetry}
            />
          </div>
          <div className={styles.close}>
            <PrimaryIconButton
              onPressed={() => manager.dismiss(item)}
              backgroundColor="transparent"
              overlayColor={CLOSE_OVERLAY_COLOR}
              iconSize={CLOSE_ICON_SIZE}
              icon="close"
              size={CLOSE_BUTTON_SIZE}
              foregroundColor="#fff"
            />
          </div>
        </div>
      ))}
    </div>,
    document.body,
  );
}
